import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Wand2, ImageIcon } from "lucide-react";
import { FaYoutube } from "react-icons/fa";
import api, { API, apiError } from "@/lib/api";

export const ThumbnailCard = ({ video, mediaToken, onUpdated }) => {
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/videos/${video.id}/thumbnail`);
      toast.success("Thumbnail ready — it'll be set automatically when you publish to YouTube.");
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const url = video.has_thumbnail && mediaToken
    ? `${API}/videos/${video.id}/thumbnail?token=${mediaToken}&v=${encodeURIComponent(video.thumbnail_updated_at || "")}`
    : null;

  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-5" data-testid="thumbnail-card">
      <p className="font-head font-semibold flex items-center gap-2 mb-1">
        <FaYoutube className="h-4 w-4 text-red-500" /> YouTube thumbnail
      </p>
      <p className="text-xs text-zinc-500 mb-3">Auto-generated when you create your video (5 credits) with your title rendered into the art — auto-set on YouTube when you publish. Regenerate anytime for a fresh look.</p>
      {url ? (
        <img src={url} alt="Video thumbnail" className="w-full aspect-video object-cover rounded-md border border-white/10 mb-3" data-testid="thumbnail-preview" />
      ) : (
        <div className="w-full aspect-video rounded-md border border-dashed border-white/15 bg-[#0A0A0A] flex flex-col items-center justify-center gap-1.5 mb-3 text-zinc-600" data-testid="thumbnail-empty">
          <ImageIcon className="h-6 w-6" />
          <span className="text-xs">No thumbnail yet</span>
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={generate}
          disabled={busy}
          data-testid="thumbnail-generate-btn"
          className="flex-1 bg-blue-600 hover:bg-blue-500 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {busy ? "Designing…" : `${video.has_thumbnail ? "Regenerate" : "Generate"} · 5 credits`}
        </button>
        {url && (
          <a
            href={url}
            download={`${(video.title || "thumbnail").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-thumbnail.jpg`}
            data-testid="thumbnail-download-btn"
            className="bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5"
          >
            <Download className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};
